import { toast } from '@avila-roffe/components-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSkill,
  createSkillRelease,
  deleteSkill,
  deleteSkillFile,
  deleteSkillRelease,
  deprecateSkillRelease,
  exportSkill,
  getSkill,
  getSkillFiles,
  getSkillRelease,
  listAllSkillReleases,
  listAttachments,
  listSkillReleases,
  listSkillsForApp,
  publishSkillRelease,
  updateSkillMetadata,
  updateSkillRelease,
  upsertSkillFile,
} from '../skillApi';
import type { SkillFile, SkillRelease } from '../types';

// ─── Query key factory ────────────────────────────────────────────────────────

export const skillKeys = {
  all: ['skills'] as const,
  byApp: (appSlug: string) => [...skillKeys.all, 'app', appSlug] as const,
  detail: (appSlug: string, name: string) => [...skillKeys.all, 'detail', appSlug, name] as const,
  files: (appSlug: string, name: string) => [...skillKeys.detail(appSlug, name), 'files'] as const,
  attachments: (resourceId: string) => [...skillKeys.all, 'attachments', resourceId] as const,
};

// ─── List ─────────────────────────────────────────────────────────────────────

export function useSkills(appSlug: string) {
  return useQuery({
    queryKey: skillKeys.byApp(appSlug),
    enabled: !!appSlug,
    queryFn: () => listSkillsForApp(appSlug),
  });
}

// ─── Detail ───────────────────────────────────────────────────────────────────

export function useSkill(appSlug: string | undefined, name: string | undefined) {
  return useQuery({
    queryKey: skillKeys.detail(appSlug ?? '', name ?? ''),
    enabled: !!appSlug && !!name,
    queryFn: () => getSkill(appSlug as string, name as string),
  });
}

// ─── Files ────────────────────────────────────────────────────────────────────

export function useSkillFiles(appSlug: string | undefined, name: string | undefined) {
  return useQuery({
    queryKey: skillKeys.files(appSlug ?? '', name ?? ''),
    enabled: !!appSlug && !!name,
    queryFn: () => getSkillFiles(appSlug as string, name as string),
  });
}

// ─── Attachments ──────────────────────────────────────────────────────────────

export function useSkillAttachments(skillId: string | undefined) {
  return useQuery({
    queryKey: skillKeys.attachments(skillId ?? ''),
    enabled: !!skillId,
    queryFn: () => listAttachments('skill', skillId as string),
  });
}

// ─── Create ───────────────────────────────────────────────────────────────────

export function useCreateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; displayName: string; appSlug: string; content?: string }) =>
      createSkill(data),
    onSuccess: (skill) => {
      void qc.invalidateQueries({ queryKey: skillKeys.byApp(skill.appSlug) });
      toast.success('Skill created');
    },
  });
}

// ─── Update metadata ─────────────────────────────────────────────────────────

export function useUpdateSkillMetadata(appSlug: string, name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof updateSkillMetadata>[2]) =>
      updateSkillMetadata(appSlug, name, payload),
    onSuccess: (updated) => {
      qc.setQueryData(skillKeys.detail(appSlug, name), updated);
      void qc.invalidateQueries({ queryKey: skillKeys.byApp(appSlug) });
      toast.success('Skill updated');
    },
  });
}

// ─── Upsert file ─────────────────────────────────────────────────────────────

export function useUpsertSkillFile(appSlug: string, name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, content }: { path: string; content: string }) =>
      upsertSkillFile(appSlug, name, path, content),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: skillKeys.files(appSlug, name) });
      toast.success('File saved');
    },
  });
}

// ─── Delete file ─────────────────────────────────────────────────────────────

export function useDeleteSkillFile(appSlug: string, name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (path: string) => deleteSkillFile(appSlug, name, path),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: skillKeys.files(appSlug, name) });
      toast.success('File deleted');
    },
  });
}

// ─── Delete ───────────────────────────────────────────────────────────────────

export function useDeleteSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ appSlug, name }: { appSlug: string; name: string }) =>
      deleteSkill(appSlug, name),
    onSuccess: (_, { appSlug }) => {
      void qc.invalidateQueries({ queryKey: skillKeys.byApp(appSlug) });
      toast.success('Skill deleted');
    },
  });
}

// ─── Export ───────────────────────────────────────────────────────────────────

export function useExportSkill(appSlug: string, name: string) {
  return useMutation({
    mutationFn: (basePath?: string) => exportSkill(appSlug, name, basePath),
    onSuccess: (result) => {
      toast.success(`Exported ${result.exportedFiles.length} files to ${result.basePath}`);
    },
  });
}

// Re-export SkillFile so callers can import it from here if needed
export type { SkillFile };

// ─── Skill Releases ───────────────────────────────────────────────────────────

export const skillReleaseKeys = {
  all: ['skill-releases'] as const,
  list: (appSlug: string, name: string) => [...skillReleaseKeys.all, appSlug, name] as const,
};

export function useSkillReleases(appSlug: string | undefined, name: string | undefined) {
  return useQuery({
    queryKey: skillReleaseKeys.list(appSlug ?? '', name ?? ''),
    enabled: !!appSlug && !!name,
    queryFn: () => listSkillReleases(appSlug as string, name as string),
  });
}

export function useCreateSkillRelease(appSlug: string, name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: { name: string; notes?: string }) => createSkillRelease(appSlug, name, dto),
    onSuccess: (newRelease) => {
      // Prepend immediately — no waiting for refetch
      qc.setQueryData<SkillRelease[]>(skillReleaseKeys.list(appSlug, name), (old = []) => [
        newRelease,
        ...old,
      ]);
      void qc.invalidateQueries({ queryKey: skillReleaseKeys.list(appSlug, name) });
      toast.success('Release created');
    },
  });
}

export function usePublishSkillRelease(appSlug: string, name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) => publishSkillRelease(appSlug, name, releaseId),
    onSuccess: (updated) => {
      qc.setQueryData<SkillRelease[]>(skillReleaseKeys.list(appSlug, name), (old = []) =>
        old.map((r) => (r.id === updated.id ? updated : r)),
      );
      void qc.invalidateQueries({ queryKey: skillReleaseKeys.list(appSlug, name) });
      toast.success('Release published');
    },
  });
}

export function useDeprecateSkillRelease(appSlug: string, name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) => deprecateSkillRelease(appSlug, name, releaseId),
    onSuccess: (updated) => {
      qc.setQueryData<SkillRelease[]>(skillReleaseKeys.list(appSlug, name), (old = []) =>
        old.map((r) => (r.id === updated.id ? updated : r)),
      );
      void qc.invalidateQueries({ queryKey: skillReleaseKeys.list(appSlug, name) });
      toast.success('Release deprecated');
    },
  });
}

export function useDeleteSkillRelease(appSlug: string, name: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) => deleteSkillRelease(appSlug, name, releaseId),
    onSuccess: (_, releaseId) => {
      qc.setQueryData<SkillRelease[]>(skillReleaseKeys.list(appSlug, name), (old = []) =>
        old.filter((r) => r.id !== releaseId),
      );
      void qc.invalidateQueries({ queryKey: skillReleaseKeys.list(appSlug, name) });
      toast.success('Release deleted');
    },
  });
}

export function useSkillRelease(
  appSlug: string | undefined,
  name: string | undefined,
  releaseId: string | undefined,
) {
  return useQuery({
    queryKey: [...skillReleaseKeys.list(appSlug ?? '', name ?? ''), releaseId ?? ''],
    enabled: !!appSlug && !!name && !!releaseId,
    queryFn: () => getSkillRelease(appSlug as string, name as string, releaseId as string),
  });
}

export function useUpdateSkillRelease(appSlug: string, name: string, releaseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name?: string; notes?: string | null }) =>
      updateSkillRelease(appSlug, name, releaseId, payload),
    onSuccess: (updated) => {
      qc.setQueryData<SkillRelease[]>(skillReleaseKeys.list(appSlug, name), (old = []) =>
        old.map((r) => (r.id === updated.id ? updated : r)),
      );
      void qc.invalidateQueries({ queryKey: skillReleaseKeys.list(appSlug, name) });
      toast.success('Release saved');
    },
  });
}

// ─── All skill releases (flat, single request) ────────────────────────────────

export const skillReleaseFlatKeys = {
  all: ['skill-releases-flat'] as const,
  filtered: (appSlug?: string, status?: string) =>
    [...skillReleaseFlatKeys.all, appSlug ?? '_all', status ?? ''] as const,
};

export function useAllSkillReleasesFlat(appSlug?: string, status?: string) {
  return useQuery({
    queryKey: skillReleaseFlatKeys.filtered(appSlug, status),
    queryFn: () => listAllSkillReleases(appSlug, status),
  });
}
